import { useLoad } from "@/lib/api";
import { Panel, Stat, Table, Health, money, day } from "@/pages/console/Kit";

function Headline() {
  const o = useLoad("/admin/overview").data;
  if (!o) return <p className="text-slate-400">Loading…</p>;
  const c = o.current, p = o.previous;
  const vs = (k) => `previous period: ${p[k]}`;
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="MRR" value={money(o.mrr)} testid="overview-mrr" />
        <Stat label="Paying customers" value={o.paying_customers} testid="overview-paying" />
        <Stat label="Paid branches" value={o.branches} testid="overview-branches" />
        <Stat label="Visitors (30 days)" value={c.visitors} sub={vs("visitors")} testid="overview-visitors" />
        <Stat label="Sign-ups started" value={c.signups_started} sub={vs("signups_started")} />
        <Stat label="Accounts created" value={c.accounts_created} sub={vs("accounts_created")} />
        <Stat label="Cancellations" value={c.cancellations} sub={vs("cancellations")} />
        <Stat label="Failed payments" value={c.failed_payments} sub={vs("failed_payments")} />
      </div>
      <Panel title="Customers most at risk" testid="overview-at-risk">
        <Table rows={o.at_risk} empty="No customers at risk." cols={[["name", "Customer"], ["health", "Health", (r) => <Health h={r.health} />], ["past_due", "Payment failing", (r) => (r.past_due ? "Yes" : "No")]]} />
      </Panel>
    </div>
  );
}

function Funnel() {
  const f = useLoad("/admin/funnel").data;
  if (!f) return null;
  const top = f.steps[0]?.count || 1;
  return (
    <Panel title="Sign-up funnel (30 days)" testid="overview-funnel">
      <div className="space-y-2">
        {f.steps.map((s) => (
          <div key={s.name} className="grid grid-cols-[180px_1fr_90px] items-center gap-3 text-sm">
            <span className="font-semibold">{s.label}</span>
            <div className="h-3 rounded-full bg-slate-100"><div className="h-3 rounded-full bg-teal-600" style={{ width: `${Math.max(2, (100 * s.count) / top)}%` }} /></div>
            <span className="text-right">{s.count}{s.lost_pct > 0 && <span className="ml-1 text-xs text-slate-400">-{s.lost_pct}%</span>}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function Revenue() {
  const r = useLoad("/admin/revenue").data;
  if (!r) return null;
  return (
    <div className="space-y-4" data-testid="overview-revenue">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="ARR" value={money(r.arr)} />
        <Stat label="Net new MRR" value={money(r.net_new_mrr)} sub={`new ${money(r.new_mrr)} · expansion ${money(r.expansion_mrr)} · churn ${money(r.churned_mrr)}`} />
        <Stat label="Avg revenue / customer" value={money(r.arpc)} sub={`LTV ${money(r.ltv)}`} />
        <Stat label="Voucher conversion" value={r.voucher_conversion_pct == null ? "-" : `${r.voucher_conversion_pct}%`} sub={`churn ${r.customer_churn_pct}%`} />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Renewals this week"><Table rows={r.renewals_this_week} cols={[["name", "Customer"], ["renews_at", "Renews", (x) => day(x.renews_at)], ["amount", "Amount", (x) => money(x.amount)]]} /></Panel>
        <Panel title="Payments at risk"><Table rows={r.payments_at_risk} cols={[["name", "Customer"], ["since", "Failing since", (x) => day(x.since)], ["amount", "Amount", (x) => money(x.amount)]]} /></Panel>
        <Panel title="Collected by month"><Table rows={r.collected_by_month} cols={[["month", "Month"], ["total", "Collected", (x) => money(x.total)]]} /></Panel>
        <Panel title="12-month MRR forecast"><Table rows={r.forecast.slice(0, 6)} cols={[["month", "Month"], ["mrr", "MRR", (x) => money(x.mrr)]]} /></Panel>
      </div>
    </div>
  );
}

export default function Overview() {
  return <div className="space-y-6"><Headline /><Funnel /><Revenue /></div>;
}
